import {
  getLocationManagersListQueryOptions,
  getManagersOnDutyListQueryKey,
  getManagersOnDutyListQueryOptions,
  type LocationManager,
  type ManagerOnDuty,
  type ForManagerLocationDetail,
} from "@sidegigllc/api/client";
import { GIGPRO_LOCATION_HEADER } from "api/constants";
import type { SectionManager } from "domains/postGig/providers/PostGigData/types";
import type { WarmableQuery } from "routing/warmQueries";

type QueriesInput = { location: Pick<ForManagerLocationDetail, "id"> };

function locationManagerToSectionManager(manager: LocationManager): SectionManager {
  return {
    id: manager.id,
    fullname: `${manager.user.first_name} ${manager.user.last_name}`.trim(),
    phone_number: manager.user.phone_number ?? "",
    role: manager.role,
    location_manager_id: manager.id, // Track that this came from a location manager
    location_id: manager.location,
    location_invite_id: manager.user.id,
  };
}

export function managerOnDutyToSectionManager(mod: ManagerOnDuty): SectionManager {
  return {
    id: mod.id,
    fullname: mod.fullname,
    phone_number: mod.phone_number,
    location_id: mod.location_id,
  };
}

/** Location managers eligible to be promoted to a manager on duty. */
export const onSiteManagersQuery = ({ location }: QueriesInput) =>
  getLocationManagersListQueryOptions(
    { location: location.id, include_pending: true },
    {
      query: {
        select: (managers) =>
          managers
            // filter out null users and active managers without phone numbers
            .filter((m) => m.user && (m.status === "pending" || m.user.phone_number))
            .map(locationManagerToSectionManager),
      },
    },
  );

// Scope to the shift's location, not the app's active one: override the location header the endpoint reads,
// and key the cache by location so responses for different locations don't collide on the shared query key.
export const onDutyManagersQuery = ({ location }: QueriesInput) =>
  getManagersOnDutyListQueryOptions({
    query: {
      queryKey: [...getManagersOnDutyListQueryKey(), location.id],
      select: (mods) =>
        mods
          .filter((mod) => mod.location_id === location.id)
          .map(managerOnDutyToSectionManager)
          .toSorted((a, b) => a.fullname.localeCompare(b.fullname, undefined, { sensitivity: "base" })),
    },
    request: { headers: { [GIGPRO_LOCATION_HEADER]: String(location.id) } },
  });

export const queries = (input: QueriesInput): Array<WarmableQuery> => [
  onSiteManagersQuery(input),
  onDutyManagersQuery(input),
];
